import { ActionTree, ActionContext } from 'vuex'
import { RootState } from '@/store/types'
import EntryRepository from '@/data/entry-repository'
import Entry from '@/data/entry-model'
import { RouteBuilder } from '@/router'
import EntryHydrator from '@/data/entry-hydrator'
import StoreAPI from '@/util/api/StoreAPI'
import { EntriesState, EntriesGetters } from './types'
import {
    FETCH_ENTRY,
    SET_ENTRY,
    FETCH_ENTRIES,
    SET_ALL_ENTRIES,
    VAULT_TREE_OPEN,
    SET_TREE_STACK,
    VAULT_TREE_CLOSE,
    VAULT_TREE_CLOSE_ALL,
    SET_MANY_ENTRIES,
    SET_SORT_ORDER,
    FETCH_UPDATES,
    REMOVE_ENTRY
} from './constants'

interface Context extends ActionContext<EntriesState, RootState> {
    getters: EntriesGetters
}

export type SortOrderUpdate = { entry: Entry, newIndex: number }

const actions: ActionTree<EntriesState, RootState> = {
    /**
     * Fetch entries from the IDB
     */
    async [FETCH_ENTRIES](
        { commit, rootState, rootGetters }: Context
    ): Promise<Entry[]> {
        const entries = await EntryRepository.fetchAll()
        entries.forEach(entry => entry.connect(rootState, rootGetters))
        commit(SET_ALL_ENTRIES, entries)
        return entries
    },

    /**
     * Fetch partial entry updates from the API
     */
    async [FETCH_UPDATES](
        { commit, dispatch, rootState, rootGetters }: Context, force: boolean = false
    ): Promise<Entry[]> {
        const entries = await EntryRepository.fetchPartials(force)
        entries.forEach(entry => entry.connect(rootState, rootGetters))
        commit(SET_MANY_ENTRIES, entries)

        EntryHydrator.start(entryId => dispatch(FETCH_ENTRY, entryId))
        return entries
    },

    /**
     * Fetch or refresh an entry
     */
    async [FETCH_ENTRY]({ commit, rootState, rootGetters }, entryId: string): Promise<Entry> {
        const entry = await EntryRepository.fetch(entryId)
        entry.connect(rootState, rootGetters)
        if (entry.deleted()) {
            commit(REMOVE_ENTRY, entry.id)
        } else {
            commit(SET_ENTRY, entry)
        }
        return entry
    },

    /**
     * Set the sort order on an entry
     */
    async [SET_SORT_ORDER](
        { commit, rootState, rootGetters }, { entry, newIndex }: SortOrderUpdate
    ): Promise<Entry> {
        const updatedData = await StoreAPI.updateSortOrder(entry.id!, newIndex)
        const storedEntry = await EntryRepository.updateModelData(updatedData)
        storedEntry.connect(rootState, rootGetters)
        commit(SET_ENTRY, storedEntry)
        return storedEntry
    },

    /**
     * Open vault in the tree
     */
    [VAULT_TREE_OPEN]({ commit, getters }: Context, entryId: string) {
        commit(SET_TREE_STACK, getters.getEntry(entryId).path)
        RouteBuilder.setTreeRoute(entryId)
    },

    /**
     * Close the deepest vault in the tree
     */
    [VAULT_TREE_CLOSE]({ state }) {
        RouteBuilder.setTreeRoute(state.treeStack[state.treeStack.length - 2])
    },

    /**
     * Close all vaults in the tree
     */
    [VAULT_TREE_CLOSE_ALL]({ commit }) {
        commit(SET_TREE_STACK, [])
        RouteBuilder.setTreeRoute()
    }
}

export default actions
